import type { FilterActions, FilterV2ActionHandler, IFilterItem } from "@next-platform/client-common-spicy-ui";
import { useCallback, useEffect, useLayoutEffect, useRef } from "react";

import type { FilterSectionV2Id } from "./FilterSectionV2Id";
import useFiltersV2Registry from "./useFiltersV2Registry";

type UseRegisterFilterSectionV2Params = {
  sectionId: FilterSectionV2Id;
  items: IFilterItem[];
  actionHandler: FilterV2ActionHandler;
  activeRefinementCount?: number;
  isEnabled?: boolean;
};

const useRegisterFilterSectionV2 = ({
  sectionId,
  items,
  actionHandler,
  activeRefinementCount = 0,
  isEnabled = true,
}: UseRegisterFilterSectionV2Params) => {
  const registry = useFiltersV2Registry();
  const refActionHandler = useRef(actionHandler);
  const callLatestActionHandler = useCallback((action: FilterActions, value?: string) => {
    if (value === undefined) {
      refActionHandler.current(action);
      return;
    }
    refActionHandler.current(action, value);
  }, []);

  useLayoutEffect(() => {
    refActionHandler.current = actionHandler;
  }, [actionHandler]);

  useEffect(() => {
    if (!registry || !isEnabled) return;
    return () => registry.unregisterSection(sectionId);
  }, [registry, sectionId, isEnabled]);

  useEffect(() => {
    if (!registry || !isEnabled) return;
    registry.registerSection({ sectionId, items, actionHandler: callLatestActionHandler, activeRefinementCount });
  }, [registry, sectionId, isEnabled, items, activeRefinementCount, callLatestActionHandler]);
};

export default useRegisterFilterSectionV2;
